"use client";

import React from "react";
import { Sparkles, ArrowRight, ShieldCheck, HeartHandshake, Compass } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const benefits = [{
  icon: Sparkles,
  label: "Ganzheitliche Reiki-Behandlung"
}, {
  icon: Compass,
  label: "Innere Ruhe & emotionale Klarheit"
}, {
  icon: HeartHandshake,
  label: "Achtsame Begleitung in Zürich, Kreis "
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden text-foreground py-20 lg:py-28 bg-background">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 size-[620px] rounded-full bg-secondary/40 blur-3xl" />
        <div className="absolute top-1/3 -right-24 size-[400px] rounded-full bg-accent/60 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 size-[450px] rounded-full bg-secondary/30 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-7 flex flex-col justify-center space-y-8">
            <motion.div variants={itemVariants} className="space-y-4">
              <Badge variant="secondary" className="px-3.5 py-1.5 rounded-md text-xs sm:text-sm font-medium tracking-wide bg-secondary text-secondary-foreground border border-border">
                <Sparkles className="size-3.5 mr-1.5 text-primary" />
                Ganzheitliche Heilung &amp; Innere Balance
              </Badge>

              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal leading-[1.12] text-foreground tracking-tight">Reiki Therapie in Zürich für emotionale Klarheit und tiefe Regeneration.</h1>
            </motion.div>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-foreground/85 leading-relaxed max-w-2xl font-sans font-light">Erlebe achtsame Energiearbeit im Herzen von Zürich. Wandelherz verbindet traditionelle Reiki-Praxis mit geerdetem spirituellem Wachstum – für nachhaltige Entspannung, Stressreduktion und neue Lebensenergie.</motion.p>

            {}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-[color,background-color,box-shadow] h-12 px-7 text-base font-medium">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Termin anfragen</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-background/80 hover:bg-secondary text-foreground hover:text-secondary-foreground shadow-xs transition-[color,background-color,border-color] h-12 px-7 text-base font-medium">
                <Link to="/AngebotePage">
                  Angebote entdecken
                </Link>
              </Button>
            </motion.div>

            {}
            <motion.div variants={itemVariants} className="pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4">
              {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return <div key={benefit.label} data-index={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                    <div className="flex items-center justify-center size-7 rounded-md bg-secondary text-secondary-foreground shrink-0">
                      <Icon className="size-3.5" />
                    </div>
                    <span className="font-sans leading-snug">{benefit.label}</span>
                  </div>;
            })}
            </motion.div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.9,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.2
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {}
              <div className="absolute -inset-3.5 rounded-xl border border-border/80 bg-muted/40 -rotate-1 pointer-events-none" />

              {}
              <div className="relative overflow-hidden rounded-lg bg-card shadow-lg border border-border">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581972/img/reiki_healing_serenity_lavender.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581972/img/reiki_healing_serenity_lavender.png-wvc-sizes" alt="Achtsame Reiki Behandlung und natürliche Harmonie bei Wandelherz" loading="eager" className="w-full h-[420px] sm:h-[480px] lg:h-[520px] object-cover object-center" src="https://wandelherz.ch/wp-content/uploads/2026/08/IMG_9722-scaled.jpg" />

                {}
                <div className="absolute inset-0 bg-linear-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />

                {}
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-md bg-background/95 backdrop-blur-md border border-border shadow-md">
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                      <ShieldCheck className="size-5" />
                    </div>
                    <div className="space-y-0.5">
                      <p className="font-serif text-base font-semibold text-foreground">
                        Raum für Transformation
                      </p>
                      <p className="text-xs text-foreground/75 leading-relaxed font-sans">
                        Individuell abgestimmte Einzelsitzungen in geschützter, ruhiger Atmosphäre.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}